import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import DataTable from '../../../components/tables/DataTable';
import SimpleDropdown from '../../../components/SimpleDropdown';
import StatusBadge from '../../../components/StatusBadge';
import {
	listProductOptions,
	listProductVariants,
	createVariant,
	removeVariant,
} from '../../../utils/queries';

const NONE = '__none__';

export default function VariantsTab({ product }) {
	const [options, setOptions] = useState([]);
	const [variants, setVariants] = useState([]);
	const [loading, setLoading] = useState(true);
	const [form, setForm] = useState({ sku: '', pointCost: '', option1ValueId: NONE, option2ValueId: NONE, option3ValueId: NONE });
	const [saving, setSaving] = useState(false);
	const [formOpen, setFormOpen] = useState(false);

	const refresh = () => {
		setLoading(true);
		return Promise.all([listProductOptions(product.productId), listProductVariants(product.productId)]).then(
			([optionsResult, variantsResult]) => {
				if (optionsResult?.success) setOptions(optionsResult.data);
				if (variantsResult?.success) setVariants(variantsResult.data);
				setLoading(false);
			}
		);
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [product.productId]);

	const valueLabel = (id) => {
		if (!id) return '-';
		for (const option of options) {
			const match = option.values.find((v) => v.productOptionValueId === id);
			if (match) return `${option.name}: ${match.value}`;
		}
		return id;
	};

	const handleAdd = async (e) => {
		e.preventDefault();
		setSaving(true);
		const payload = {
			sku: form.sku || undefined,
			pointCost: form.pointCost === '' ? undefined : Number(form.pointCost),
			option1ValueId: form.option1ValueId === NONE ? undefined : form.option1ValueId,
			option2ValueId: form.option2ValueId === NONE ? undefined : form.option2ValueId,
			option3ValueId: form.option3ValueId === NONE ? undefined : form.option3ValueId,
		};
		const result = await createVariant(product.productId, payload);
		setSaving(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to add variant.');
			return;
		}
		setForm({ sku: '', pointCost: '', option1ValueId: NONE, option2ValueId: NONE, option3ValueId: NONE });
		setFormOpen(false);
		refresh();
	};

	const handleRemove = async (id) => {
		await removeVariant(id);
		refresh();
	};

	const columns = [
		{ key: 'sku', header: 'SKU' },
		{ key: 'option1ValueId', header: 'Option 1', render: (v) => valueLabel(v) },
		{ key: 'option2ValueId', header: 'Option 2', render: (v) => valueLabel(v) },
		{ key: 'pointCost', header: 'Point Cost' },
		{ key: 'status', header: 'Status', render: (v) => <StatusBadge status={v === 'active' ? 'active' : 'inactive'} text={v} /> },
		{
			key: 'productVariantId',
			header: '',
			sortable: false,
			render: (id) => (
				<button type='button' onClick={() => handleRemove(id)}>
					<Trash2 size={20} />
				</button>
			),
		},
	];

	const optionDropdown = (optionIndex, key) => {
		const option = options[optionIndex];
		if (!option) return null;
		return (
			<div className='flex flex-col' key={key}>
				<label>{option.name}</label>
				<SimpleDropdown
					value={form[key]}
					onChange={(v) => setForm({ ...form, [key]: v })}
					options={[{ value: NONE, label: '-' }, ...option.values.map((v) => ({ value: v.productOptionValueId, label: v.value }))]}
					className='w-40'
				/>
			</div>
		);
	};

	const emptyHeadingContent = !formOpen ? (
		<>
			No variants yet{' '}
			<button type='button' className='underline text-black hover:no-underline' onClick={() => setFormOpen(true)}>
				Add a variant
			</button>
		</>
	) : (
		'No variants yet'
	);

	return (
		<div className='max-w-3xl'>
			<DataTable columns={columns} data={variants} loading={loading} emptyHeading={emptyHeadingContent} />

			{!formOpen && !loading && variants.length > 0 && (
				<button
					type='button'
					className='btn-secondary-small mt-5 rounded-full p-1.5'
					aria-label='Add'
					onClick={() => setFormOpen(true)}
				>
					<Plus className='size-4' />
				</button>
			)}

			{formOpen && (
				<form onSubmit={handleAdd} className='flex flex-wrap items-end gap-3 mt-5'>
					<div className='flex flex-col'>
						<label>SKU</label>
						<input className='form-input w-40' value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
					</div>
					<div className='flex flex-col'>
						<label>Point Cost</label>
						<input
							type='number'
							className='form-input w-32'
							value={form.pointCost}
							onChange={(e) => setForm({ ...form, pointCost: e.target.value })}
						/>
					</div>
					{optionDropdown(0, 'option1ValueId')}
					{optionDropdown(1, 'option2ValueId')}
					{optionDropdown(2, 'option3ValueId')}
					<button type='submit' disabled={saving} className='btn-primary h-10'>
						{saving ? 'Adding...' : 'Add Variant'}
					</button>
					<button
						type='button'
						className='btn-secondary h-10'
						onClick={() => setFormOpen(false)}
					>
						Cancel
					</button>
				</form>
			)}
			{formOpen && options.length === 0 && (
				<p className='text-xs text-gray-400 mt-3'>
					Add options (e.g. Color) in the Options tab to enable variant selection dropdowns.
				</p>
			)}
		</div>
	);
}
